/**
 * The item-line editor - Dry Cleaning and Shoe Care (FRD §8).
 *
 * These services are priced PER GARMENT with a manually entered price, so they
 * cannot use the weight flow. This is the one place the brief allows a modal:
 * a room's garment list is a variable-length thing, and inlining it in a grid
 * row would make every row a different height.
 *
 * THE RATE CARD IS A DEFAULT, NEVER A RULE. Picking an item pre-fills the
 * client's negotiated price, and the operator may then type over it. That
 * override belongs to THIS LINE ONLY - it must never be written back to the
 * card, or one unusual room would silently reprice every future room.
 *
 * Edits are held locally and only handed back on "Update room total", so
 * Cancel really cancels.
 */
import { useMemo, useState } from 'react'
import { Trash2 } from 'lucide-react'

import { useColors } from '../../../hooks/useColors'
import { Modal, PillButton } from './primitives'
import { newDraftItem, toNumber } from '../draft'
import type { DraftItemLine } from '../draft'
import { lineAmount, linesTotal } from './itemLineMath'
import { formatMoney } from '../format'
import { serviceBySlug } from '../serviceCatalogue'
import type { B2bRateCard } from '../types'

export interface ItemLinesModalProps {
  roomNumber: string
  serviceSlug: string
  /** Lines the room already has for this service. */
  lines: DraftItemLine[]
  rateCard: B2bRateCard
  onCancel: () => void
  onApply: (lines: DraftItemLine[]) => void
}

const GRID = '1fr 74px 108px 84px 32px'

export default function ItemLinesModal({
  roomNumber,
  serviceSlug,
  lines,
  rateCard,
  onCancel,
  onApply,
}: ItemLinesModalProps) {
  const colors = useColors()
  const service = serviceBySlug(serviceSlug)

  // Copied on open so Cancel discards cleanly.
  const [draftItems, setDraftItems] = useState<DraftItemLine[]>(() => lines.map((line) => ({ ...line })))

  const addItem = (itemName: string) =>
    setDraftItems((current) => [...current, newDraftItem(serviceSlug, itemName, rateCard)])

  const editItem = (id: string, field: 'itemName' | 'quantity' | 'unitPrice', value: string) =>
    setDraftItems((current) =>
      current.map((line) => {
        if (line.id !== id) return line
        if (field === 'itemName') {
          // Switching the garment re-fills the client's price for the new one -
          // an operator changing Shirt to Saree wants Saree's rate, not Shirt's.
          return { ...line, itemName: value, unitPrice: newDraftItem(serviceSlug, value, rateCard).unitPrice }
        }
        return { ...line, [field]: value }
      }),
    )

  const removeItem = (id: string) =>
    setDraftItems((current) => current.filter((line) => line.id !== id))

  const problems = useMemo(
    () =>
      new Map(
        draftItems.map((line) => {
          const quantity = toNumber(line.quantity)
          const price = toNumber(line.unitPrice)
          return [
            line.id,
            {
              quantity: !Number.isInteger(quantity) || quantity < 1,
              price: price <= 0,
            },
          ]
        }),
      ),
    [draftItems],
  )

  const hasProblem = [...problems.values()].some((problem) => problem.quantity || problem.price)
  const total = linesTotal(draftItems)

  return (
    <Modal
      title={service?.label ?? serviceSlug}
      onClose={onCancel}
      width="640px"
      footer={
        <>
          <PillButton variant="secondary" onClick={onCancel}>
            Cancel
          </PillButton>
          <PillButton
            onClick={() => onApply(draftItems)}
            disabled={hasProblem}
            title={hasProblem ? 'Fix the highlighted lines first' : undefined}
          >
            Update room total
          </PillButton>
        </>
      }
    >
      <p
        className="font-body"
        style={{
          margin: '0 0 2px',
          fontSize: '10px',
          fontWeight: 700,
          letterSpacing: '0.9px',
          textTransform: 'uppercase',
          color: colors.textMuted,
        }}
      >
        Room {roomNumber} · item-based service
      </p>
      <p className="font-body" style={{ margin: '0 0 14px', fontSize: '12px', color: colors.textSecondary }}>
        Select items, enter quantity and adjust the price directly.
      </p>

      {/* Quick-add chips, straight from the client's catalogue. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          flexWrap: 'wrap',
          padding: '10px 0',
          borderTop: `1px solid ${colors.border}`,
          borderBottom: `1px solid ${colors.border}`,
        }}
      >
        <span
          className="font-body"
          style={{
            fontSize: '10px',
            fontWeight: 700,
            letterSpacing: '0.9px',
            textTransform: 'uppercase',
            color: colors.textMuted,
          }}
        >
          Items
        </span>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', justifyContent: 'flex-end' }}>
          {(service?.items ?? []).map((item) => (
            <button
              key={item}
              type="button"
              className="font-body"
              onClick={() => addItem(item)}
              style={{
                height: '28px',
                padding: '0 9px',
                borderRadius: '6px',
                border: `1px solid ${colors.border}`,
                background: colors.bgCard,
                fontSize: '11px',
                fontWeight: 600,
                color: colors.textPrimary,
                cursor: 'pointer',
              }}
            >
              + {item}
            </button>
          ))}
        </div>
      </div>

      {draftItems.length === 0 ? (
        <div style={{ padding: '34px 0', textAlign: 'center' }}>
          <p className="font-body" style={{ margin: 0, fontSize: '13px', fontWeight: 700 }}>
            No item selected
          </p>
          <p className="font-body" style={{ margin: '4px 0 0', fontSize: '11px', color: colors.textSecondary }}>
            Add one or more items above to continue.
          </p>
        </div>
      ) : (
        <div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: GRID,
              gap: '8px',
              padding: '8px 0',
              borderBottom: `1px solid ${colors.border}`,
              fontSize: '9px',
              fontWeight: 700,
              letterSpacing: '0.8px',
              textTransform: 'uppercase',
              color: colors.textMuted,
            }}
          >
            <span>Item</span>
            <span>Quantity</span>
            <span>Manual price</span>
            <span style={{ textAlign: 'right' }}>Amount</span>
            <span />
          </div>

          {draftItems.map((line) => {
            const problem = problems.get(line.id)
            const amount = lineAmount(line)

            return (
              <div
                key={line.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: GRID,
                  gap: '8px',
                  alignItems: 'start',
                  padding: '10px 0',
                  borderBottom: `1px solid ${colors.surface}`,
                }}
              >
                <select
                  value={line.itemName}
                  aria-label={`Item for line ${line.id}`}
                  onChange={(event) => editItem(line.id, 'itemName', event.target.value)}
                  className="font-body"
                  style={{
                    height: '32px',
                    borderRadius: '6px',
                    border: `1px solid ${colors.border}`,
                    background: colors.bgCard,
                    padding: '0 8px',
                    fontSize: '11px',
                    fontWeight: 600,
                    color: colors.textPrimary,
                  }}
                >
                  {(service?.items ?? []).map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>

                <input
                  value={line.quantity}
                  aria-label={`Quantity for ${line.itemName}`}
                  aria-invalid={problem?.quantity || undefined}
                  inputMode="numeric"
                  onChange={(event) => editItem(line.id, 'quantity', event.target.value)}
                  className="font-body"
                  style={{
                    height: '32px',
                    borderRadius: '6px',
                    border: `1px solid ${problem?.quantity ? '#991B1B' : colors.border}`,
                    background: colors.bgCard,
                    padding: '0 8px',
                    fontSize: '11px',
                    fontWeight: 600,
                    color: problem?.quantity ? '#991B1B' : colors.textPrimary,
                  }}
                />

                <div>
                  <div style={{ position: 'relative' }}>
                    <span
                      aria-hidden
                      style={{
                        position: 'absolute',
                        left: '8px',
                        top: '7px',
                        fontSize: '11px',
                        color: colors.textMuted,
                        pointerEvents: 'none',
                      }}
                    >
                      ₹
                    </span>
                    <input
                      value={line.unitPrice}
                      aria-label={`Price for ${line.itemName}`}
                      aria-invalid={problem?.price || undefined}
                      inputMode="decimal"
                      onChange={(event) => editItem(line.id, 'unitPrice', event.target.value)}
                      className="font-body"
                      style={{
                        width: '100%',
                        height: '32px',
                        borderRadius: '6px',
                        border: `1px solid ${problem?.price ? '#991B1B' : colors.border}`,
                        background: colors.bgCard,
                        paddingLeft: '19px',
                        paddingRight: '8px',
                        fontSize: '11px',
                        fontWeight: 600,
                        color: problem?.price ? '#991B1B' : colors.textPrimary,
                      }}
                    />
                  </div>
                  {problem?.price ? (
                    <p
                      className="font-body"
                      style={{ margin: '3px 0 0', fontSize: '9px', lineHeight: 1.3, color: 'var(--chip-danger-ink)' }}
                    >
                      Price required
                    </p>
                  ) : null}
                </div>

                <span
                  className="font-body"
                  style={{ paddingTop: '8px', textAlign: 'right', fontSize: '11px', fontWeight: 700 }}
                >
                  {amount > 0 ? formatMoney(amount) : '-'}
                </span>

                <button
                  type="button"
                  aria-label={`Remove ${line.itemName}`}
                  onClick={() => removeItem(line.id)}
                  style={{
                    display: 'grid',
                    placeItems: 'center',
                    height: '32px',
                    width: '28px',
                    borderRadius: '5px',
                    border: 'none',
                    background: 'transparent',
                    color: colors.textMuted,
                    cursor: 'pointer',
                  }}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            )
          })}
        </div>
      )}

      <div
        style={{
          marginTop: '14px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 14px',
          borderRadius: '9px',
          border: '1px solid rgba(214,185,123,0.45)',
          background: 'rgba(214,185,123,0.14)',
        }}
      >
        <span className="font-body" style={{ fontSize: '12px', fontWeight: 700 }}>
          {service?.label ?? serviceSlug} total
        </span>
        <span
          className="font-display"
          data-testid="item-lines-total"
          style={{ fontSize: '20px', fontWeight: 700 }}
        >
          {formatMoney(total)}
        </span>
      </div>
    </Modal>
  )
}
